import React from 'react';
import { SVGIcon, SvgIconType } from '@uikit/old/Icon/Icon';

import PageHeader from '@uikit/old/PageHeader/PageHeader';
import urls from '@constants/urls';

import FeeCallout from './_components/FeeCallout/FeeCallout';
import FeeTable from './_components/FeeTable/FeeTable';
import SwiftCountryFeeTable from './_components/SwiftCountryFeeTable/SwiftCountryFeeTable';

import {
  CONVERSION_FEE_COLUMNS,
  CONVERSION_FEE_ROWS,
  EUR_SWIFT_COLUMNS,
  EUR_SWIFT_ROWS,
  LOCAL_RAILS_COLUMNS,
  LOCAL_RAILS_ROWS,
  METHOD_SUMMARIES,
  PAGE_SECTIONS,
  RECEIVING_FEE_COLUMNS,
  RECEIVING_FEE_ROWS,
  RECOMMENDATIONS,
  SOURCE_LINKS,
  SUMMARY_TABLE_COLUMNS,
  SUMMARY_TABLE_ROWS,
  SWIFT_SPECIAL_COLUMNS,
  SWIFT_SPECIAL_ROWS,
  WISE_DIRECT_COLUMNS,
  WISE_DIRECT_NOTES,
  WISE_DIRECT_ROWS,
} from './constants';

import './WithdrawalFeesPage.scss';

const METHOD_ICONS: Record<string, SvgIconType> = {
  wise: 'zap',
  'bank-transfer': 'landmark',
  paypal: 'walletOutline',
};

function scrollToSection(sectionId: string): void {
  document
    .getElementById(`withdrawal-fees-${sectionId}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function WithdrawalFeesPage(): JSX.Element {
  const renderSectionNav = () => (
    <nav className="withdrawal-fees__nav" aria-label="Page sections">
      {PAGE_SECTIONS.map((section) => (
        <button
          key={section.id}
          type="button"
          className="withdrawal-fees__nav-chip"
          onClick={() => scrollToSection(section.id)}
        >
          {section.title}
        </button>
      ))}
    </nav>
  );

  const renderMethodCards = () => (
    <div className="withdrawal-fees__method-cards">
      {METHOD_SUMMARIES.map((method) => {
        const methodIcon = METHOD_ICONS[method.id];
        return (
          <div key={method.id} className="withdrawal-fees__method-card">
            <div className="withdrawal-fees__method-card-top">
              <span className="withdrawal-fees__method-icon">
                <SVGIcon icon={methodIcon} size={20} />
              </span>
              <span className="withdrawal-fees__method-badge">{method.badge}</span>
            </div>
            <div className="withdrawal-fees__method-title">{method.title}</div>
            <div className="withdrawal-fees__method-fee">{method.headlineFee}</div>
            <p className="withdrawal-fees__method-description">{method.description}</p>
            <div className="withdrawal-fees__method-best-for">
              <span>Best for:</span> {method.bestFor}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderRecommendations = () => (
    <ol className="withdrawal-fees__recommendations">
      {RECOMMENDATIONS.map((recommendation, index) => (
        <li key={recommendation.title} className="withdrawal-fees__recommendation">
          <span className="withdrawal-fees__recommendation-number">{index + 1}</span>
          <div>
            <div className="withdrawal-fees__recommendation-title">{recommendation.title}</div>
            <p className="withdrawal-fees__recommendation-text">{recommendation.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );

  const renderSources = () => (
    <ul className="withdrawal-fees__sources">
      {SOURCE_LINKS.map((source) => (
        <li key={source.url}>
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="withdrawal-fees__source-link"
          >
            <SVGIcon icon="externalLinkOutline" size={14} />
            <span>{source.title}</span>
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="withdrawal-fees">
      <PageHeader
        title="Withdrawal fees"
        withBackArrow
        backArrowForceLink={urls.SETTINGS_BILLING}
      />

      <div className="withdrawal-fees__content">
        <div className="withdrawal-fees__inner">
          <p className="withdrawal-fees__lead">
            Fees for each withdrawal method available in the Respona Publisher portal: Wise, bank
            transfer, and PayPal. All Wise figures are taken from Wise&apos;s official published
            pricing. Wise reviews its fees regularly, so the exact fee for a specific withdrawal is
            always shown at payout time and may differ slightly.
          </p>

          <FeeCallout>
            <strong>Note on exchange rates:</strong> Wise always converts at the mid-market rate
            with no markup - all conversion costs are charged as an explicit fee, not hidden in the
            rate. PayPal applies its own exchange rate for cross-currency payments.
          </FeeCallout>

          {renderSectionNav()}

          <section
            id="withdrawal-fees-summary"
            className="withdrawal-fees__section"
            aria-labelledby="withdrawal-fees-summary-title"
          >
            <h2 id="withdrawal-fees-summary-title" className="withdrawal-fees__section-title">
              Summary of withdrawal methods
            </h2>
            {renderMethodCards()}
            <div className="withdrawal-fees__card">
              <FeeTable columns={SUMMARY_TABLE_COLUMNS} rows={SUMMARY_TABLE_ROWS} />
            </div>
          </section>

          <section id="withdrawal-fees-wise" className="withdrawal-fees__section">
            <h2 className="withdrawal-fees__section-title">
              Wise - direct transfer to a Wise account
            </h2>
            <p className="withdrawal-fees__text">
              The cheapest withdrawal option. Funds are sent from our Wise Business account directly
              to your Wise account.
            </p>
            <div className="withdrawal-fees__card">
              <FeeTable columns={WISE_DIRECT_COLUMNS} rows={WISE_DIRECT_ROWS} />
            </div>
            <ul className="withdrawal-fees__notes">
              {WISE_DIRECT_NOTES.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </section>

          <section id="withdrawal-fees-bank-transfer" className="withdrawal-fees__section">
            <h2 className="withdrawal-fees__section-title">Bank transfer</h2>
            <p className="withdrawal-fees__text">
              Fees depend on the destination country, the receiving currency, and the payment rail
              Wise uses. There are three main cases.
            </p>

            <h3 className="withdrawal-fees__subsection-title">
              USD to a US bank account (domestic, ACH)
            </h3>
            <p className="withdrawal-fees__text">
              A flat <strong>1.13 USD</strong> per payout from a Wise balance to a US bank account.
              No percentage fee applies, and receiving ACH payments is free for the recipient.
            </p>

            <h3 className="withdrawal-fees__subsection-title">
              Local-currency payout (USD converted, paid out on local rails)
            </h3>
            <p className="withdrawal-fees__text">
              For most countries, Wise converts USD to your local currency and pays out through the
              local banking network (SEPA in Europe, Faster Payments in the UK, PayID/NPP in
              Australia, UPI/NEFT in India, etc.). No SWIFT receiving fees apply. The fee is a
              conversion fee (percentage of the amount) plus a small fixed fee in the target
              currency. Representative rates for a 1,000 USD payout (February 2026 data):
            </p>
            <div className="withdrawal-fees__card">
              <FeeTable columns={LOCAL_RAILS_COLUMNS} rows={LOCAL_RAILS_ROWS} />
            </div>
            <FeeCallout>
              <strong>Large withdrawal discount:</strong> amounts over the equivalent of 25,000 USD
              per month get an automatic discount on the variable fee, from 0.1% (25k-375k USD) up
              to 0.17% (1.25M+ USD).
            </FeeCallout>

            <h3 className="withdrawal-fees__subsection-title">
              USD via SWIFT (recipient receives USD outside the US)
            </h3>
            <p className="withdrawal-fees__text">
              If you want to receive USD (not your local currency) to a bank account outside the US,
              Wise uses the SWIFT network and charges a fixed fee per transfer that depends on the
              destination country. A small base Wise fee (~1.13 USD when funded from a USD balance)
              is added on top - for example, a 4,000 USD transfer with no predicted correspondent
              fees costs 8.54 USD total.
            </p>
            <div className="withdrawal-fees__card">
              <FeeTable columns={SWIFT_SPECIAL_COLUMNS} rows={SWIFT_SPECIAL_ROWS} />
            </div>
            <FeeCallout type="warning">
              <strong>Important for recipients:</strong> if Wise does not prepay correspondent fees
              for your destination, intermediary banks and/or your own bank may deduct their own
              fees from the amount (commonly 10-25 USD). Make sure your receiving account is
              denominated in USD, otherwise your bank may auto-convert at a poor rate.
            </FeeCallout>

            <h3 className="withdrawal-fees__subsection-title">
              Per-country USD SWIFT fees (correspondent fees predicted)
            </h3>
            <div className="withdrawal-fees__card withdrawal-fees__card--padded">
              <SwiftCountryFeeTable />
            </div>

            <h3 className="withdrawal-fees__subsection-title">
              EUR via SWIFT (recipient receives EUR outside the SEPA zone)
            </h3>
            <div className="withdrawal-fees__card">
              <FeeTable columns={EUR_SWIFT_COLUMNS} rows={EUR_SWIFT_ROWS} />
            </div>
            <p className="withdrawal-fees__text">
              Conversion fees are added on top if the source currency is not EUR.
            </p>

            <h3 className="withdrawal-fees__subsection-title">
              GBP via SWIFT (recipient receives GBP outside the UK)
            </h3>
            <p className="withdrawal-fees__text">
              GBP can be sent to GBP-denominated accounts (IBAN or account number format) in most
              countries worldwide via SWIFT. Wise adds the SWIFT cost to the regular Wise fee; the
              exact amount is shown at transfer setup. The recipient&apos;s bank and correspondent
              banks may charge their own receiving fees.
            </p>
          </section>

          <section id="withdrawal-fees-paypal" className="withdrawal-fees__section">
            <h2 className="withdrawal-fees__section-title">PayPal</h2>
            <p className="withdrawal-fees__text">
              A flat <strong>2% fee</strong> is charged on all withdrawals via PayPal, regardless of
              amount, currency, or country. The fee is deducted from the withdrawal amount.
            </p>
            <FeeCallout type="warning">
              If your PayPal account is held in a currency other than USD, PayPal converts the funds
              using its own exchange rate, which includes a currency conversion spread (typically
              3%-4% on top of the base rate). This conversion cost is charged by PayPal, not by
              Respona.
            </FeeCallout>
          </section>

          <section id="withdrawal-fees-conversion-fees" className="withdrawal-fees__section">
            <h2 className="withdrawal-fees__section-title">Wise conversion fee reference</h2>
            <p className="withdrawal-fees__text">
              Wise&apos;s official conversion fee range is <strong>0.22%-4.42%</strong> of the
              converted amount, varying by currency pair. Rules of thumb:
            </p>
            <div className="withdrawal-fees__card">
              <FeeTable columns={CONVERSION_FEE_COLUMNS} rows={CONVERSION_FEE_ROWS} />
            </div>
            <FeeCallout>
              A variable volatility fee (0%-10%) may apply in rare cases of extreme exchange-rate
              volatility for certain currencies, per Wise&apos;s dynamic-charges policy.
            </FeeCallout>
          </section>

          <section id="withdrawal-fees-receiving-fees" className="withdrawal-fees__section">
            <h2 className="withdrawal-fees__section-title">Fees on the receiving side</h2>
            <p className="withdrawal-fees__text">
              These fees are charged by the recipient&apos;s institution, not by Respona, but they
              affect the net amount received:
            </p>
            <div className="withdrawal-fees__card">
              <FeeTable columns={RECEIVING_FEE_COLUMNS} rows={RECEIVING_FEE_ROWS} />
            </div>
          </section>

          <section id="withdrawal-fees-recommendations" className="withdrawal-fees__section">
            <h2 className="withdrawal-fees__section-title">Recommendations for publishers</h2>
            {renderRecommendations()}
          </section>

          <section className="withdrawal-fees__section withdrawal-fees__section--sources">
            <h2 className="withdrawal-fees__section-title">Sources</h2>
            {renderSources()}
          </section>
        </div>
      </div>
    </div>
  );
}

export default WithdrawalFeesPage;
